import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { red } from "@mui/material/colors";
import SettingsIcon from "@mui/icons-material/Settings";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MemoryIcon from "@mui/icons-material/Memory";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { debounce } from "lodash";
import { format } from "date-fns";

import fpOrderTrackingService from "@plannel/services/fp/fp-order-tracking-service";
import FlowChart from "@plannel/pages/factory-plan/common/component/FlowChart";
import { orderTrackingDataToFlow } from "@plannel/pages/factory-plan/common/flowchart";
import SplitterContainer from "@plannel/components/SplitterContainer";
import reduxUtil from "@plannel/utils/redux-util";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, { buildFlatReportStatusBar } from "@plannel/pages/factory-plan/common/grid";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — 다른 Report 화면과 동일 결정(fp-report-screen-guide.md §A-3c).
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰는 필드 3개 — OrderTrackingQueryRepository#makeWhereClause가
// stageCdNm/resourceCdNm/woCdNm 전부 contains 조건으로 소비하는 살아있는 필터.
const SEARCH_FIELD_LABELS = { stageCdNm: "FP_STAGE", resourceCdNm: "FP_RESOURCE", woCdNm: "FP_ORDER_CD" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

// 트리 레벨(AG Grid node.level, 0부터) — old iconCallback의 RealGrid getLevel(1부터)을 그대로 옮긴 것.
// level 0(공장) = gear, level 1(작업주문) = cube(정상)/warning(지연), level 2+(공정/재고, BOM 깊이만큼
// 재귀) = cpu. old iconImages = ["gear_24.png","cube_yellow_24.png","optionpane_warning.png","cpu.png"].
const PLANT_LEVEL = 0;
const ORDER_LEVEL = 1;

// 지연 판정 — mainCol styleCallback(red-text), 트리 아이콘, 하단 다이어그램의 problemStatus 강조가
// 모두 이 기준을 공유한다.
const isLateOrder = (data) => {
  if (!data?.endTs || !data?.dueDt) return false;
  return new Date(data.endTs).getTime() > new Date(data.dueDt).getTime();
};

const TreeLevelIcon = ({ level, late }) => {
  if (level === PLANT_LEVEL) return <SettingsIcon fontSize="small" sx={{ color: "text.secondary" }} />;
  if (level === ORDER_LEVEL) {
    return late
      ? <WarningAmberIcon fontSize="small" color="warning" />
      : <Inventory2Icon fontSize="small" sx={{ color: "warning.light" }} />;
  }
  return <MemoryIcon fontSize="small" sx={{ color: "text.secondary" }} />;
};

// old dataProvider.setObjectRows({rows: responseData}, 'rows', ...) — 백엔드가 내려주는 건 DeliveryStatus의
// treeId 경로 문자열이 아니라 각 노드가 자기 자식을 `rows`에 재귀적으로 담은 진짜 트리(OrderTreeResultDto)다.
// AG Grid Tree Data는 경로 배열(getDataPath)을 요구하므로 재귀 순회하며 조상 체인을 경로로 만들어 평탄화한다.
// 경로 세그먼트는 `mainCol__형제인덱스`로 만들어 형제간 mainCol 중복이 있어도 경로가 항상 고유하다
// (표시값은 valueGetter가 data.mainCol을 직접 읽으므로 이 접미사가 화면에 노출되지 않는다).
// old TreeGrid의 childProperty가 'rows' 고정이라 `byproducts`(부산물 목록)는 old도 트리에 렌더하지 않던
// 필드라 그대로 버린다(기능 유실 아님, old 파리티).
const flattenOrderTree = (nodes, parentPath = []) => {
  const result = [];
  (nodes ?? []).forEach((node, index) => {
    const path = [...parentPath, `${node.mainCol ?? index}__${index}`];
    result.push({
      mainCol: node.mainCol,
      routeCd: node.routeCd, routeNm: node.routeNm,
      simultaneousResources: node.simultaneousResources,
      uom: node.uom, qty: node.qty,
      startTs: node.startTs, endTs: node.endTs, dueDt: node.dueDt,
      isStock: node.isStock,
      path
    });
    if (node.rows?.length) result.push(...flattenOrderTree(node.rows, path));
  });
  return result;
};

const OrderTracking = ({ t, viewName, title }) => {
  const reduxDispatch = useDispatch();
  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });

  const stageCdNmRef = useRef("");
  const resourceCdNmRef = useRef("");
  const woCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  // 하단 다이어그램(old OrderTrackingDiagram) — 그리드에서 작업주문 행을 더블클릭하면 그린다.
  // 백엔드 응답의 원본 트리(작업주문 노드)는 그리드 평탄화 과정에서 사라지므로, 다이어그램용으로
  // 작업주문 레벨 노드만 따로 들고 있는다(old orderTrackingData ref와 동일한 역할).
  const orderNodesRef = useRef([]);
  const [diagramFlow, setDiagramFlow] = useState({ nodes: [], edges: [] });

  const latestReqRef = useRef(0);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveOrderTracking(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      treeData: true,
      getDataPath: (data) => data.path,
      // old expandAll(1) — 공장(level0) 노드만 펼쳐서 작업주문(level1)까지 보이고, 그 아래(공정/재고)는
      // 접힌 채로 시작한다.
      groupDefaultExpanded: 1
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old valueCallback — routeCd+routeNm을 "코드 (명칭)"로 합친다. routeCd만 있으면 코드만, 둘 다
  // 없으면 빈칸(옛 동작 그대로).
  const routeValueGetter = (params) => {
    const { routeCd, routeNm } = params.data ?? {};
    if (routeCd && routeNm) return `${routeCd} (${routeNm})`;
    if (routeCd) return routeCd;
    return "";
  };

  const columnDefs = useMemo(() => [
    { headerName: "FP_INVENTORY_ROUTE_STOCK", colId: "route", width: 300, valueGetter: routeValueGetter, type: ["nonEditableColumn"] },
    { headerName: "FP_RESOURCE", field: "simultaneousResources", colId: "simultaneousResources", width: 300, type: ["nonEditableColumn"] },
    { headerName: "UOM", field: "uom", colId: "uom", width: 90, type: ["nonEditableColumn", "centerAligned"] },
    { headerName: "QTY", field: "qty", colId: "qty", width: 110, type: ["nonEditableColumn", "numberColumn", "rightAligned"] },
    { headerName: "FP_START_TS", field: "startTs", colId: "startTs", width: 180, type: ["nonEditableColumn", "dateTimeColumn", "centerAligned"] },
    { headerName: "FP_END_TS", field: "endTs", colId: "endTs", width: 180, type: ["nonEditableColumn", "dateTimeColumn", "centerAligned"] },
    { headerName: "DUE_DATE", field: "dueDt", colId: "dueDt", width: 180, type: ["nonEditableColumn", "dateTimeColumn", "centerAligned"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const autoGroupColumnDef = useMemo(() => ({
    headerName: t("FP_ORDER_BOM"),
    minWidth: 420,
    pinned: "left",
    type: ["nonEditableColumn"],
    valueGetter: (params) => params.data?.mainCol ?? params.node?.key,
    cellStyle: (params) => (
      params.node?.level === ORDER_LEVEL && isLateOrder(params.data) ? { color: red[500] } : null
    ),
    cellRendererParams: {
      innerRenderer: (params) => {
        const level = params.node?.level ?? 0;
        const late = level === ORDER_LEVEL && isLateOrder(params.data);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <TreeLevelIcon level={level} late={late} />
            <span>{params.value}</span>
          </Box>
        );
      }
    }
  }), [t]);

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const retrieveOrderTracking = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestReqRef.current;
    const params = {
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCds: version.plantCd,
      stageCdNm: stageCdNmRef.current,
      resourceCdNm: resourceCdNmRef.current,
      woCdNm: woCdNmRef.current,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };

    gridRef.current?.api?.setGridOption("loading", true);

    fpOrderTrackingService.getOrdersTree(params)
      .then(response => {
        if (reqId !== latestReqRef.current) return;

        const rows = flattenOrderTree(response.data ?? []);
        setRowData(rows);

        // old loadData의 targetData — 공장(level0) 아래 작업주문(level1) 노드만 평평하게 모아둔다.
        orderNodesRef.current = (response.data ?? []).flatMap(plant => plant.rows ?? []);
        setDiagramFlow({ nodes: [], edges: [] });   // old: 재조회 시 다이어그램 초기화

        if (rows.length === 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        if (reqId === latestReqRef.current) setRowData([]);
        debug(e);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  // 하위 그룹 노드 전체를 재귀 순회한다(펼침/접힘 양쪽에서 재사용).
  const forEachDescendantGroup = (node, fn) => {
    node.childrenAfterGroup?.forEach(child => {
      if (!child.group) return;
      fn(child);
      forEachDescendantGroup(child, fn);
    });
  };

  // old onTreeItemExpanded(RealGrid tree.expand(itemIndex, true, true))는 **레벨 구분 없이** 한 노드를
  // 펼치면 하위 전체를 재귀 전개했다. new는 공장 레벨만 예외로 둔다(2026-08-12 사용자 지시):
  //  - 공장(level 0) 펼침 → 바로 아래 작업주문까지만. 이전에 펼쳐둔 하위가 남아 같이 튀어나오지
  //    않도록 하위 전체를 명시적으로 접는다(AG Grid는 노드별 펼침 상태를 계속 기억한다).
  //  - 작업주문(level 1) 이하 펼침 → old대로 하위 전체(공정/재고, BOM 깊이만큼) 재귀 전개.
  // 공장은 작업주문이 수십~수백 건이라 전체 전개가 실질적으로 쓸 수 없는 화면이 되기 때문.
  const onRowGroupOpened = (params) => {
    if (!params.expanded) return;

    if (params.node?.level === PLANT_LEVEL) {
      forEachDescendantGroup(params.node, child => child.setExpanded(false));
      return;
    }

    forEachDescendantGroup(params.node, child => child.setExpanded(true));
  };

  // old onCellDblClicked — 작업주문 행을 더블클릭하면 그 주문의 공정 흐름을 하단 다이어그램에 그린다.
  // old는 mainCol 컬럼에서만 반응했지만, 트리 컬럼의 colId(`ag-Grid-AutoColumn`)는 AG Grid가
  // 내부적으로 부여하는 값이라(AutoGroupColumnDef는 colId 지정 자체가 불가) 그 문자열에 기대지 않고
  // **행 조건**(작업주문 레벨 + 납기 있음)으로 판정한다 — old 조건(dueDt !== null)의 상위집합이라
  // 어느 컬럼을 더블클릭해도 열린다.
  const onCellDoubleClicked = (params) => {
    if (params.node?.level !== ORDER_LEVEL || !params.data?.dueDt) return;

    const orderCd = params.data.mainCol;
    const orderNode = orderNodesRef.current.find(row => row.mainCol === orderCd);
    if (!orderNode) return;

    setDiagramFlow(orderTrackingDataToFlow(orderNode, t, versionState.planScope));
  };

  // 다이어그램 노드 더블클릭 → 해당 기준정보 화면으로 딥링크 이동(fp-master-screen-guide.md §13-1).
  const onDiagramNodeDoubleClick = (event, node) => {
    const nav = node?.data?.nav;
    if (!nav?.reduxKey) return;
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(nav.reduxKey, nav.fields)));
    reduxDispatch(reduxUtil.updateHistoryState(nav.reduxKey));
  };

  const statusBar = useMemo(() => buildFlatReportStatusBar(), []);

  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = new Date(startDate.getTime() + SEARCH_ZONE_DAYS * 24 * 60 * 60 * 1000);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();
    setRowData([]);
    orderNodesRef.current = [];
    setDiagramFlow({ nodes: [], edges: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b). 자유 텍스트
  // (stageCdNm/resourceCdNm/woCdNm)는 SearchOptsGroupRetrieve 내부 수동 커밋으로만 조회된다.
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodState.startDate, periodState.endDate, periodState.isEntirePlanPeriod, versionState.plantCd]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          useBucket={false}
        />
        <SearchOptsGroupRetrieve
          fieldRefs={{ stageCdNm: stageCdNmRef, resourceCdNm: resourceCdNmRef, woCdNm: woCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveOrderTracking}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old ResultArea sizes={[50,50]} direction="vertical" — 그리드 위 / 다이어그램 아래 1:1 분할
          (SplitterContainer 기본값 [3,2]는 60/40이라 명시적으로 1:1을 준다, 다른 Report 화면과 동일). */}
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-order-tracking-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            autoGroupColumnDef={autoGroupColumnDef}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowGroupOpened={onRowGroupOpened}
            onCellDoubleClicked={onCellDoubleClicked}
          />
        </Box>
        <Box id="fp-order-tracking-diagram" className="grid-area" sx={{ height: "calc(100% - 8px)", mt: 1, minHeight: 0 }}>
          <FlowChart
            nodes={diagramFlow.nodes}
            edges={diagramFlow.edges}
            onNodeDoubleClick={onDiagramNodeDoubleClick}
          />
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(OrderTracking);
